import { validiteParDefaut } from './echeances';
import { tauxTvaParDefaut } from './tva';
import { auCentime, totauxDocument } from './vat';
import { nextSeq, today, uid } from './utils';

/**
 * Lecteur de soumissions (Pack IA) : ce que l'écran fait de la lecture.
 *
 * Le serveur lit la soumission, rapproche les postes du catalogue et dresse le
 * rapport de vigilance ; il ne garde rien. L'écran tient le brouillon, le
 * laisse modifier, en calcule les totaux, puis le convertit en offre. Les
 * fonctions de ce module ne touchent ni au réseau ni à l'écran.
 */

/**
 * Clé du réglage « Corps de métier », dans les paramètres de la société.
 *
 * <p>Un seul réglage JSON `{generale, metiers, autre}` porte ce que la maquette
 * range en trois champs de la société (`soumGenerale`, `metiersSoum`,
 * `metierAutre`). Les métiers y sont des clés (`carrelage`), non des libellés :
 * c'est ce que le serveur relit pour filtrer les postes (`CorpsDeMetier`).
 * Le réglage voyage tel quel dans le sac de paramètres (`api/mappers.js`).
 */
export const CLE_CORPS_METIER = 'corpsMetier';

/**
 * Les corps de métier, dans l'ordre de l'écran : [clé, libellé].
 *
 * La liste vit aussi sur le serveur (`CorpsDeMetier`) : les deux doivent dire
 * la même chose, clés comprises.
 */
export const METIERS_SOUMISSION = [
  ['maconnerie', 'Maçonnerie'],
  ['menuiserie', 'Menuiserie'],
  ['charpente', 'Charpente'],
  ['carrelage', 'Carrelage'],
  ['platrerie', 'Plâtrerie'],
  ['peinture', 'Peinture'],
  ['sanitaire', 'Sanitaire'],
  ['chauffage', 'Chauffage'],
  ['ventilation', 'Ventilation'],
  ['electricite', 'Électricité'],
  ['toiture', 'Toiture / étanchéité'],
  ['ferblanterie', 'Ferblanterie'],
  ['serrurerie', 'Serrurerie'],
  ['vitrerie', 'Vitrerie'],
  ['sols', 'Sols / parquet'],
  ['isolation', 'Isolation'],
  ['terrassement', 'Terrassement'],
  ['paysagisme', 'Paysagisme'],
  ['nettoyage', 'Nettoyage'],
];

/** Libellé d'un corps de métier. */
export function libelleMetier(cle) {
  if (cle === 'generaux') return 'Généralités';
  const trouve = METIERS_SOUMISSION.find(([k]) => k === cle);
  return trouve ? trouve[1] : cle || '';
}

/**
 * Le réglage, tel que lu dans les paramètres, rendu sûr.
 *
 * @param {object} [valeur] réglage enregistré
 * @returns {{generale: boolean, metiers: string[], autre: string}} le réglage
 */
export function lireReglageMetier(valeur) {
  const cles = new Set(METIERS_SOUMISSION.map(([k]) => k));
  return {
    generale: valeur?.generale === true,
    metiers: Array.isArray(valeur?.metiers) ? valeur.metiers.filter((m) => cles.has(m)) : [],
    autre: typeof valeur?.autre === 'string' ? valeur.autre : '',
  };
}

/** Coche ou décoche un métier. */
export function basculeMetier(reglage, cle) {
  const r = lireReglageMetier(reglage);
  const metiers = r.metiers.includes(cle)
    ? r.metiers.filter((m) => m !== cle)
    : [...r.metiers, cle];
  return { ...r, metiers };
}

/**
 * Le réglage en une phrase, pour l'option « mon métier ».
 *
 * @returns {string} « Carrelage, Plâtrerie », « tout le dossier », ou '' si rien n'est réglé
 */
export function resumeReglage(reglage) {
  const r = lireReglageMetier(reglage);
  if (r.generale) return 'tout le dossier (entreprise générale)';
  const noms = r.metiers.map(libelleMetier);
  if (r.autre.trim()) noms.push(r.autre.trim());
  return noms.join(', ');
}

/** Dix mégaoctets par fichier, comme une photo de chantier. */
export const OCTETS_FICHIER = 10 * 1024 * 1024;

/** Quatorze au total : encodée, la requête tient sous les vingt admis. */
export const OCTETS_TOTAL = 14 * 1024 * 1024;

/** Pages photographiées par lecture. */
export const PAGES_MAX = 20;

/** Extensions d'un fichier d'échange texte (SIA 451, IfA'92…). */
const EXTENSIONS_NORMALISEES = /\.(txt|sia|0[1-9]s|d8[1-6]|x8[1-6]|xml)$/i;

/** Vrai pour un fichier normalisé de soumission, à lire comme texte. */
export function estFichierNormalise(fichier) {
  return EXTENSIONS_NORMALISEES.test(fichier?.name || '') || /^text\//.test(fichier?.type || '');
}

/**
 * Vérifie un lot de fichiers avant l'envoi.
 *
 * @param {Array<{name: string, size: number, type: string}>} fichiers fichiers choisis
 * @param {{texteAdmis?: boolean, document?: string}} [options] vrai si un fichier normalisé
 *   est accepté ; le document lu, avec son article, pour le message de refus
 * @returns {string} ce qui ne va pas, ou '' si le lot peut partir
 */
export function verifieFichiers(fichiers, { texteAdmis = true, document = 'Un catalogue' } = {}) {
  const liste = fichiers || [];
  if (!liste.length) return 'Choisis un fichier.';
  if (liste.length > PAGES_MAX)
    return `${PAGES_MAX} pages au plus par lecture ; importe la suite à part.`;
  const seul = liste.some((f) => f.type === 'application/pdf' || estFichierNormalise(f));
  if (seul && liste.length > 1)
    return 'Un PDF ou un fichier normalisé se lit seul. Pour plusieurs pages, joignez des photos.';
  if (!texteAdmis && liste.some(estFichierNormalise))
    return document + ' se lit en PDF ou en photos.';
  if (liste.some((f) => f.size > OCTETS_FICHIER))
    return 'Un fichier dépasse la taille autorisée de 10 Mo.';
  return '';
}

/** Taille totale admise, vérifiée après réduction des photos. */
export function depasseLeTotal(octets) {
  return octets > OCTETS_TOTAL
    ? 'Les fichiers dépassent 14 Mo au total. Réduis les photos, ou importe le document en plusieurs fois.'
    : '';
}

/**
 * Le brouillon d'offre tiré de la lecture.
 *
 * Seuls les postes retenus par le filtre deviennent des lignes ; les autres
 * restent à portée, dans `ecartes`, pour être repris d'un geste.
 *
 * @param {object} lu réponse du serveur
 * @returns {object} le brouillon
 */
export function brouillonDepuisLecture(lu) {
  const postes = lu?.postes || [];
  return {
    titre: lu?.titre || '',
    maitreOuvrage: lu?.maitreOuvrage || '',
    source: lu?.source || '',
    filtre: lu?.filtre || '',
    metiers: lu?.metiers || [],
    alertes: lu?.alertes || [],
    chapitresEcartes: lu?.chapitresEcartes || [],
    controleTexte: lu?.controleTexte === true,
    lignes: postes.filter((p) => p.retenu).map(ligneDepuisPoste),
    ecartes: postes.filter((p) => !p.retenu).map(ligneDepuisPoste),
    remisePct: 0,
    clientId: '',
  };
}

/** Une ligne du brouillon, depuis un poste lu. */
export function ligneDepuisPoste(p) {
  return {
    id: uid(),
    code: p.codeCan || '',
    chapitre: p.chapitre || '',
    chapitreTitre: p.chapitreTitre || '',
    d: p.designation || '',
    u: p.unite || '',
    q: p.quantite ?? '',
    // Vide, non zéro : un prix à saisir ne doit pas se lire « 0.00 ».
    p: p.prixUnitaire ?? '',
    rem: 0,
    tva: p.tauxTva ?? null,
    nature: p.nature || 'normal',
    pm: p.quantitePm === true,
    horsTotal: p.horsTotal === true,
    corpsMetier: p.corpsMetier || '',
    alertes: p.alertes || [],
    rapprochement: p.rapprochement || null,
    ref: p.rapprochement?.reference || undefined,
  };
}

/** Une ligne vierge, ajoutée à la main. */
export function ligneVierge() {
  return {
    id: uid(),
    code: '',
    d: '',
    u: 'pce',
    q: 1,
    p: '',
    rem: 0,
    tva: null,
    nature: 'normal',
    pm: false,
    horsTotal: false,
    alertes: [],
    rapprochement: null,
  };
}

/** Vrai si la ligne attend encore son prix. */
export const prixASaisir = (l) => !l.horsTotal && (l.p === '' || l.p == null);

/**
 * Totaux du brouillon : Σ quantité × prix (− remise), TVA ventilée par taux.
 *
 * Les variantes et les options ne comptent pas ; une quantité « pm » vaut zéro
 * tant qu'elle n'est pas saisie. Le calcul est celui des devis
 * (`totauxDocument`), pour que l'offre convertie dise le même montant.
 *
 * @param {object[]} lignes lignes du brouillon
 * @param {number} tauxSociete taux appliqué aux lignes sans taux propre
 * @param {number} [remisePct] remise globale
 * @returns {object} totaux, ventilation, nombre de prix à saisir et de lignes hors total
 */
export function totauxSoumission(lignes, tauxSociete, remisePct) {
  const liste = lignes || [];
  const comptees = liste
    .filter((l) => !l.horsTotal)
    .map((l) => ({ q: +l.q || 0, p: +l.p || 0, rem: +l.rem || 0, tva: l.tva }));
  return {
    ...totauxDocument(comptees, tauxSociete, remisePct),
    aSaisir: liste.filter(prixASaisir).length,
    horsTotal: liste.filter((l) => l.horsTotal).length,
  };
}

/** Montant d'une ligne, remise déduite. */
export const montantLigne = (l) => auCentime((+l.q || 0) * (+l.p || 0) * (1 - (+l.rem || 0) / 100));

/**
 * Majore tous les prix saisis d'un pourcentage : la marge appliquée d'un geste.
 *
 * @param {object[]} lignes lignes du brouillon
 * @param {number} pct majoration, en pour-cent (négative pour baisser)
 * @returns {object[]} les lignes, prix arrondis au centime ; les prix vides restent vides
 */
export function majorePrix(lignes, pct) {
  const facteur = 1 + (+pct || 0) / 100;
  return (lignes || []).map((l) =>
    l.p === '' || l.p == null ? l : { ...l, p: auCentime((+l.p || 0) * facteur) },
  );
}

/**
 * L'offre officielle tirée du brouillon, prête à rejoindre la collection des devis.
 *
 * Les variantes et options partent en lignes à zéro, désignation préfixée :
 * elles restent lisibles par le client sans gonfler le total.
 *
 * @param {object} brouillon brouillon vérifié
 * @param {{devis: object[], soc: object}} contexte devis existants et société
 * @returns {object} le devis, au format de l'écran des offres
 */
export function versDevis(brouillon, { devis, soc }) {
  const num = nextSeq(devis);
  const lignes = (brouillon.lignes || []).map((l) => ({
    id: uid(),
    d: (l.horsTotal ? (l.nature === 'option' ? 'Option — ' : 'Variante — ') : '') + (l.d || ''),
    sd: l.code ? 'Pos. CAN ' + l.code : '',
    u: l.u || '',
    q: l.horsTotal ? 0 : +l.q || 0,
    p: +l.p || 0,
    rem: +l.rem || 0,
    tva: l.tva ?? null,
    ref: l.ref,
  }));
  return {
    id: num,
    clientId: brouillon.clientId || '',
    desc: brouillon.titre || 'Offre sur soumission',
    date: today(),
    delai: '',
    intro: brouillon.maitreOuvrage ? 'Soumission : ' + brouillon.maitreOuvrage : '',
    mentions: '',
    validite: validiteParDefaut(soc),
    acompte: soc?.acompteDefaut ?? 0,
    tvaPct: tauxTvaParDefaut(soc),
    remisePct: +brouillon.remisePct || 0,
    lignes,
    statut: 'En attente',
    signDate: null,
    bonCommande: false,
    sect: 'batiment',
  };
}

/**
 * Les prestations à ajouter au catalogue : postes chiffrés à la main, avec leur
 * code CAN, que le catalogue ne connaît pas encore. La prochaine soumission les
 * reconnaîtra par leur code.
 *
 * @param {object} brouillon brouillon vérifié
 * @param {object[]} catalogue prestations existantes
 * @returns {object[]} les nouvelles prestations, au format de l'écran
 */
export function prestationsAApprendre(brouillon, catalogue) {
  const codes = new Set((catalogue || []).map((a) => a.codeCan).filter(Boolean));
  let rang = (catalogue || []).reduce(
    (m, x) => Math.max(m, +String(x.id).replace(/\D/g, '') || 0),
    0,
  );
  const nouvelles = [];
  for (const l of brouillon.lignes || []) {
    if (l.rapprochement || !l.code || codes.has(l.code) || l.p === '' || l.p == null || l.horsTotal)
      continue;
    codes.add(l.code);
    rang += 1;
    nouvelles.push({
      id: 'A' + rang,
      metier: libelleMetier(l.corpsMetier) || 'À classer',
      nom: l.d,
      u: l.u || 'forfait',
      p: +l.p || 0,
      sd: '',
      codeCan: l.code,
    });
  }
  return nouvelles;
}

/**
 * Le message qui accompagne l'offre envoyée par e-mail ou WhatsApp.
 *
 * @returns {{titre: string, message: string}} objet et corps
 */
export function messageOffre(brouillon, totaux, soc) {
  const titre = 'Offre — ' + (brouillon.titre || 'soumission');
  const lignes = (brouillon.lignes || [])
    .filter((l) => !l.horsTotal)
    .slice(0, 40)
    .map((l) => `- ${l.code ? l.code + ' ' : ''}${l.d} : ${l.q || 'pm'} ${l.u} × ${l.p || '—'}`);
  const message =
    'Bonjour,\n\nVoici notre offre pour : ' +
    (brouillon.titre || 'votre soumission') +
    '.\n\n' +
    lignes.join('\n') +
    ((brouillon.lignes || []).length > 40 ? '\n- …' : '') +
    `\n\nTotal HT : CHF ${totaux.ht.toFixed(2)}\nTVA : CHF ${totaux.tva.toFixed(2)}\nTotal TTC : CHF ${totaux.ttc.toFixed(2)}` +
    '\n\nCordialement,\n' +
    (soc?.nom || '');
  return { titre, message };
}

/**
 * Le brouillon en cours, gardé en mémoire le temps de la vérification.
 *
 * Une soumission est un document sensible : rien n'en est écrit dans le
 * navigateur. Le brouillon survit à un changement de page, pas à un
 * rechargement, et il est lié à la société et au compte qui l'ont lu — un
 * autre compte, sur le même appareil, ne le retrouve pas.
 */
let enMemoire = null;

/** Garde le brouillon pour cette session. */
export function gardeBrouillon(cle, brouillon) {
  enMemoire = brouillon ? { cle, brouillon } : null;
}

/** Reprend le brouillon de cette session, s'il y en a un. */
export function reprendBrouillon(cle) {
  return enMemoire && enMemoire.cle === cle ? enMemoire.brouillon : null;
}

/** Oublie le brouillon. */
export function oublieBrouillon() {
  enMemoire = null;
}
